import { Component } from '@theme/component';
import { prefersReducedMotion, debounce } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [mediaList] - The media list, which scrolls horizontally on mobile.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  /**
   * AP: master-image fetches in flight, keyed by the slide `<img>` each one will upgrade. A
   * disconnect drops them so a late `onload` can never write into a detached slide.
   * @type {Map<HTMLImageElement, HTMLImageElement>}
   */
  #pendingMasters = new Map();

  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    // On mobile the media list scrolls horizontally instead of the dialog
    this.refs.mediaList?.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('cancel', this.#handleDialogCancel);
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
    // AP: a slide's drag-zoom-wrapper reports when it crosses 1x; the dialog mirrors that
    // state so CSS can fade the thumbnail rail and lock slide scrolling while zoomed.
    this.addEventListener(ThemeEvents.zoomChange, this.#handleZoomChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.mediaList?.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('cancel', this.#handleDialogCancel);
    this.refs.dialog.removeEventListener('close', this.#handleDialogClose);
    this.removeEventListener(ThemeEvents.zoomChange, this.#handleZoomChange);
    // AP: lifecycle symmetry - a debounced scroll still pending or a master fetch still in
    // flight must not run against a detached dialog.
    this.handleScroll.cancel();
    this.#cancelPendingMasters();
  }

  /**
   * AP: mirrors a wrapper's zoomed state onto the dialog element.
   * @param {Event & { detail?: { zoomed: boolean } }} event
   */
  #handleZoomChange = (event) => {
    const zoomed = Boolean(event.detail?.zoomed);
    this.refs.dialog.toggleAttribute('data-zoomed', zoomed);

    // AP: zoom intent (click, double-tap, keyboard or pinch past 1x) is the only trigger for
    // the master image. Upstream fetched it speculatively on open, on every slide scroll and
    // on thumbnail / grid-tile hover: one full master per slide, on every device, whether or
    // not the visitor ever zoomed. A slide now keeps its responsive pick until it is zoomed.
    if (!zoomed || !(event.target instanceof Element)) return;

    const slide = event.target.closest('li[data-media-id]');
    if (slide instanceof HTMLElement) this.loadHighResolutionImage(slide);
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event.preventDefault();

    const { dialog, thumbnails } = this.refs;

    this.#isClosing = false;
    dialog.classList.remove('dialog-closing');
    thumbnails.classList.add('dialog-thumbnails-list--instant');
    dialog.showModal();
    this.selectThumbnail(index, { behavior: 'instant' });
    this.#syncVideoPlayback(index);
    thumbnails.getBoundingClientRect();

    requestAnimationFrame(() => {
      thumbnails.classList.remove('dialog-thumbnails-list--instant');
    });
  }

  /**
   * Upgrades a slide's image to its master (`data_max_resolution`) in place.
   *
   * AP (rewritten 8 Oct 2026): upstream built a bare `new Image()` carrying only `src`, `alt`,
   * `class` and the data attribute and `replaceWith`'d it in, dropping `width`/`height`,
   * `srcset`/`sizes`, `loading`, `fetchpriority` and the focal-point style; it keyed "done" on
   * the URL in a Set that outlived gallery morphs; and it fetched the master even when the
   * srcset had just delivered the same pixels. Now the element is kept and only `src`/`srcset`
   * change (an instant swap, the loader has already put the bytes in the memory cache); the
   * state lives on the element as `data-ap-master`, which a morph strips, so it resets with the
   * markup; the fetch is skipped when the browser's current pick already is the master URL
   * (product-media.liquid canonicalises that URL onto the 3840 rung for masters no wider than
   * it) or when the visitor asked for reduced data; and a late result is dropped if the slide
   * was morphed to other media or the dialog disconnected meanwhile.
   *
   * @param {HTMLElement} mediaContainer - The slide (`li[data-media-id]`)
   * @returns {boolean} Whether a master fetch was started
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || image.dataset.apMaster) return false;
    if (/** @type {any} */ (navigator).connection?.saveData) return false;

    // The responsive pick already is the master: nothing to fetch and nothing to swap
    const resolvedUrl = new URL(highResolutionUrl, document.baseURI).href;
    if (image.currentSrc === resolvedUrl && image.complete && image.naturalWidth > 0) {
      image.dataset.apMaster = 'loaded';
      return false;
    }

    image.dataset.apMaster = 'loading';

    const loader = new Image();

    const settle = () => {
      loader.onload = loader.onerror = null;
      this.#pendingMasters.delete(image);
    };

    loader.onload = () => {
      settle();
      // A morph has stripped the state or re-pointed the slide at other media: stale result
      if (image.dataset.apMaster !== 'loading') return;
      if (image.getAttribute('data_max_resolution') !== highResolutionUrl) return;

      // Same URL the loader just fetched, so the swap comes from cache: no flash, no gap.
      // srcset goes first so the master is selected as a plain src rather than re-picked from
      // the candidate list. Chrome may still report a density-corrected naturalWidth after the
      // swap (measured 8 Oct 2026, either order); rendering uses the full bitmap regardless, so
      // audit the delivered rung through currentSrc, never naturalWidth.
      image.removeAttribute('srcset');
      image.src = highResolutionUrl;
      image.dataset.apMaster = 'loaded';
    };

    loader.onerror = () => {
      settle();
      // Keep the responsive image and let the next zoom retry
      if (image.dataset.apMaster === 'loading') delete image.dataset.apMaster;
    };

    this.#pendingMasters.set(image, loader);
    loader.src = highResolutionUrl;

    return true;
  }

  /** AP: drops every in-flight master fetch so a late `onload` can never touch a detached slide. */
  #cancelPendingMasters() {
    for (const [image, loader] of this.#pendingMasters) {
      loader.onload = loader.onerror = null;
      if (image.dataset.apMaster === 'loading') delete image.dataset.apMaster;
    }
    this.#pendingMasters.clear();
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#setActiveThumbnail(activeIndex);

    this.#syncVideoPlayback(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    dialog.classList.add('dialog-closing');

    await this.#waitForDialogAnimation();

    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#setActiveThumbnail(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });
    }
    this.#syncVideoPlayback(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Plays the active media's video and pauses the other videos, when video autoplay is enabled.
   * Safe to call repeatedly — iOS Safari can deny the first play() attempt, so slide-change
   * retries are what get the video going.
   * @param {number} activeIndex - The index of the active media.
   */
  #syncVideoPlayback(activeIndex) {
    if (!this.hasAttribute('data-video-autoplay') || !this.refs.dialog.open) return;

    this.refs.media.forEach((mediaElement, index) => {
      const deferredMedia = /** @type {import('./media').DeferredMedia | null} */ (
        mediaElement.querySelector('deferred-media')
      );
      if (!deferredMedia) return;

      if (index === activeIndex) {
        deferredMedia.loadContent(false);

        // iOS Safari only allows playback to start outside a user gesture (e.g. when the
        // slide change comes from scrolling) for muted inline videos.
        const video = deferredMedia.querySelector('video');
        if (video) {
          video.muted = true;
          video.playsInline = true;
        }

        deferredMedia.playMedia();
      } else if (deferredMedia.isPlaying) {
        deferredMedia.pauseMedia();
      }
    });
  }

  #setActiveThumbnail(index) {
    const { thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    thumbnails.style.setProperty('--active-thumbnail-line-x', `${targetThumbnail.offsetLeft}px`);
    thumbnails.style.setProperty('--active-thumbnail-line-y', `${targetThumbnail.offsetTop}px`);
    thumbnails.style.setProperty('--active-thumbnail-line-width', `${targetThumbnail.offsetWidth}px`);
    thumbnails.style.setProperty('--active-thumbnail-line-height', `${targetThumbnail.offsetHeight}px`);
  }

  #waitForDialogAnimation() {
    const { dialog } = this.refs;

    return new Promise((resolve) => {
      let settled = false;
      let fallback = 0;

      const finish = () => {
        if (settled) return;

        settled = true;
        window.clearTimeout(fallback);
        dialog.removeEventListener('animationend', handleAnimationEnd);
        resolve();
      };

      const handleAnimationEnd = (event) => {
        if (event.target !== dialog) return;

        finish();
      };

      fallback = window.setTimeout(finish, 600);
      dialog.addEventListener('animationend', handleAnimationEnd);
    });
  }

  #handleDialogClose = () => {
    this.#isClosing = false;
    this.refs.dialog.classList.remove('dialog-closing');
  };

  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.close();
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
